/**
 * The wire contract, transcribed from `apps/handbook-mcp/app/schemas/workspace.py`.
 *
 * Nothing here is invented. Where the artboard wants a field the API does not have — an
 * "overrides a handbook rule" reference, a person's display name, a count of connected
 * assistants — the field is absent here too, and the screen that wanted it says so rather than
 * faking it.
 */

/** `SessionResponse`. The refresh token is in the body on issue and on every rotation. */
export interface Session {
  access_token: string;
  refresh_token: string;
  token_type: "bearer";
  /** Seconds until the access token expires. The service says: refresh on this, not on a 401. */
  expires_in: number;
}

/** `AccountResponse`. No display name exists on this model — the artboard's "Alex Kim" has no source. */
export interface Account {
  id: string;
  email: string;
  workspace_id: string;
  workspace_name: string;
  created_at: string;
  last_login_at: string | null;
}

/** `AccountDeletedResponse`. */
export interface AccountDeleted {
  deleted_at: string;
  purge_due_at: string;
  tokens_revoked: number;
  notes_deleted: number;
}

// --- Notes ----------------------------------------------------------------

/**
 * `NoteCategory`. The four categories the service exposes, and the only four.
 *
 * The design's earlier export drew six — it added `skill` and `agent` — and the current export
 * draws these four, which is what `NoteCreate.category` accepts. They are the same four words the
 * handbook itself is filed under: a rule your agents adhere to, a guide they follow, a flow they
 * orchestrate, knowledge they hold in mind.
 */
export type NoteCategory = "rule" | "flow" | "guide" | "knowledge";

/**
 * `NoteGrade`. MUST / MUST NOT / SHOULD / SHOULD NOT / MAY.
 *
 * The contract rejects a grade on anything that is not a rule (`_Categorised._grade_belongs_to_a_rule`),
 * so the editor only offers one on a rule and clears it when the category moves away.
 */
export type NoteGrade = "MUST" | "MUST NOT" | "SHOULD" | "SHOULD NOT" | "MAY";

/**
 * `NoteResponse`.
 *
 * There is no "overrides a handbook rule", and that one is not merely missing: the picker as
 * drawn was a dropdown of the handbook's own rule identifiers, which is an enumeration of the
 * corpus. ADR-0010 forbids it, so it stays unbuilt even if a field appears — and the current
 * design export no longer draws it either.
 */
export interface Note {
  id: string;
  workspace_id: string;
  title: string;
  category: NoteCategory;
  /** Null on anything that is not a rule; the API refuses the combination outright. */
  grade: NoteGrade | null;
  body: string;
  tags: string[];
  status: "draft" | "published";
  created_at: string;
  updated_at: string;
  published_at: string | null;
  /** When this note was last served to an agent. Records *when*, never *who*. */
  last_returned_at: string | null;
  /** Advisory only. The note reads like instructions aimed at an agent; it publishes either way. */
  flagged: boolean;
  flag_reasons: string[];
}

/**
 * The five states the design filters by, derived rather than stored.
 *
 * `status` and `flagged` come off the record. "Edited since publishing" is the one that has to be
 * computed: a published note whose `updated_at` has moved past its `published_at`. It matters more
 * than it looks — it is the state in which the agent is still being served the *older* text, and
 * the editor has to say so.
 */
export type NoteState = "draft" | "published" | "edited" | "flagged";

export function noteState(note: Note): NoteState {
  if (note.flagged) return "flagged";
  if (note.status === "draft") return "draft";
  if (note.published_at !== null && note.updated_at > note.published_at) return "edited";
  return "published";
}

/** Whether publishing would put something new in front of an agent. */
export function hasUnpublishedChanges(note: Note): boolean {
  if (note.status === "draft") return true;
  return note.published_at !== null && note.updated_at > note.published_at;
}

// --- Personal access tokens ----------------------------------------------

/** `TokenResponse`. Carries no value and cannot: only a SHA-256 digest is stored. */
export interface PersonalToken {
  id: string;
  name: string;
  workspace_id: string;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

/** `TokenCreatedResponse`. The only response anywhere that carries a token value. */
export interface CreatedToken extends PersonalToken {
  /** Shown once. There is no endpoint that could return it again. */
  value: string;
}

export type TokenState = "revoked" | "never-used" | "active";

export function tokenState(token: PersonalToken): TokenState {
  if (token.revoked_at !== null) return "revoked";
  if (token.last_used_at === null) return "never-used";
  return "active";
}

// --- The install screen ---------------------------------------------------

/** `StatusNote`. The note that answered, named. */
export interface StatusNote {
  id: string;
  title: string;
  last_returned_at: string;
}

/**
 * `StatusResponse`. Three states, decided on the server.
 *
 * Note what is *not* here: any count of connected clients. The design's "2 assistants connected"
 * has no source — `call_count` counts calls, not callers.
 */
export interface WorkspaceStatus {
  state: "waiting" | "connected" | "answering";
  workspace_id: string;
  call_count: number;
  handbook_search_count: number;
  first_seen_at: string | null;
  last_seen_at: string | null;
  note: StatusNote | null;
  /** How stale these counters may be. The UI says so rather than implying they are live. */
  counter_lag_seconds: number;
}

// --- Usage, export and onboarding ----------------------------------------
//
// `UsageResponse`, `ExportResponse`, `ExportNote` and `OnboardingResponse` were transcribed here
// and are not any more. The current design export draws no screen for a usage meter, an export or
// a setup guide, and by the rule in AGENTS.md absent from the export means removed. The endpoints
// are untouched and still documented in `apps/handbook-mcp/app/schemas/workspace.py` — transcribe
// from there if a screen for one comes back, rather than from memory or from git history.
